import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { DollarAvatar, useDollar } from "@/lib/dollar";
import type { DashboardResponse } from "@/lib/dashboard";
import { money } from "@/lib/format";

interface AlertRow { id: string; alert_type: string; title: string; body: string; is_seen: boolean; dismissed_at: string | null; created_at: string }
interface Msg { rank: number; at: number; title: string; body: string; alertId?: string; key: string }

const ALERT_RANK: Record<string, number> = { category_over: 5, unassigned_money: 6 };
const span = (m: number) => (m < 1 ? `${Math.max(0, Math.round(m * 52 / 12))} semanas` : `${Math.round(m)} ${Math.round(m) === 1 ? "mes" : "meses"}`);

function ruleMessages(d: DashboardResponse): Msg[] {
  const out: Msg[] = [];
  const m = d.liquidity.months_covered;
  const spent = d.month_summary.expense_total;
  const add = (rank: number, key: string, title: string, body: string) => out.push({ rank, at: 0, title, body, key });
  if (m !== null && m < 1) add(1, "cushion-low", "Tu colchón es muy chico", `Tu liquidez cubre ${span(m)} de gastos. Apartar algo cada quincena, aunque sea poco, es lo que más te protege.`);
  const debtPct = d.planned_income > 0 ? (d.planned_debt ?? 0) / d.planned_income : 0;
  if (debtPct > 0.3) add(1.5, "debt-high", "Más de 30% de tu ingreso se va en deudas", `Tus pagos de deudas suman ${money(d.planned_debt ?? 0)} al mes, ${Math.round(debtPct * 100)}% de tu ingreso. Bajar ese porcentaje libera dinero para tu colchón.`);
  if ((d.card_interest_month ?? 0) > 0) add(2, "card-interest", "Tus tarjetas te cobraron intereses", `Este mes pagaste ${money(d.card_interest_month ?? 0)} en intereses de tarjetas. Liquidarlas completas es el ahorro más rápido.`);
  if (d.planned_expense > 0 && spent > d.planned_expense) add(3, "over-total", "Vas arriba de tu presupuesto", `Llevas ${money(spent - d.planned_expense)} más de lo que presupuestaste en gastos este mes.`);
  if (m !== null && m >= 1 && m < 3) add(4, "cushion-mid", "Tu colchón va creciendo", `Tu liquidez cubre ${span(m)} de gastos. La meta es llegar a 3 y luego a 6.`);
  const over = d.budget.find((c) => c.status === "se_paso");
  if (over) add(5, "cat-over", `${over.name} se pasó del plan`, `Llevas ${money(over.actual_month_amount)} de ${money(over.effective_limit)}.`);
  if (d.budget.length === 0) add(6, "no-budget", "Arma tu presupuesto", "Con él sabrás cuánto puedes gastar cada mes sin sorpresas.");
  add(7, "all-good", "Todo va bien", "Vas dentro de tu presupuesto y tu colchón está sano. Sigue así.");
  return out;
}

export function pickMessage(d: DashboardResponse, alerts: AlertRow[]): Msg {
  const fromAlerts = alerts.filter((a) => !a.is_seen && !a.dismissed_at).map((a) => ({
    rank: ALERT_RANK[a.alert_type] ?? 6.5, at: Date.parse(a.created_at) || 1, title: a.title, body: a.body, alertId: a.id, key: a.alert_type }));
  return [...fromAlerts, ...ruleMessages(d)].sort((x, y) => x.rank - y.rank || y.at - x.at)[0];
}

export function MentorStrip({ d }: { d: DashboardResponse }) {
  const { state } = useDollar();
  const nav = useNavigate();
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  useEffect(() => {
    api.get<AlertRow[]>("/alerts", { params: { month: d.month } }).then((r) => setAlerts(r.data)).catch(() => undefined);
  }, [d.month]);
  const msg = pickMessage(d, alerts);
  const ask = () => {
    if (msg.alertId) api.post(`/alerts/${msg.alertId}/seen`).catch(() => undefined);
    nav("/mentor");
  };
  return (
    <div className="flex flex-col gap-4 rounded-[24px] border border-[#E7DECF] bg-[#FAF7F1] p-5 sm:flex-row sm:items-center sm:p-6" data-testid="mentor-strip" data-msg={msg.key}>
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <DollarAvatar stage={state?.stage ?? "puppy"} className="h-14 w-14" />
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#7A6548]">Tu mentor · hoy</p>
          <p className="mt-1 font-semibold text-[#16294D]" data-testid="mentor-strip-title">{msg.title}</p>
          <p className="mt-1 text-sm text-slate-600" data-testid="mentor-strip-text">{msg.body}</p>
        </div>
      </div>
      <Button onClick={ask} className="h-10 shrink-0 rounded-full bg-[#16294D] px-5 text-white hover:bg-[#2C4F91]" data-testid="mentor-strip-ask">
        <MessageCircle className="mr-2 h-4 w-4" /> Pregúntale a Dollar
      </Button>
    </div>
  );
}
